import axios from 'axios';
import { getDevDate } from '../utils/dateUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Request interceptor to add auth token and dev date
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        const deviceToken = localStorage.getItem('family_device_token');
        if (deviceToken) {
          config.headers['X-Device-Token'] = deviceToken;
        }
        const devDate = getDevDate();
        if (devDate) {
          config.headers['X-Dev-Date'] = devDate;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const code = error.response?.data?.code;

        // Device auth required - redirect to family login
        if (code === 'DEVICE_AUTH_REQUIRED') {
          localStorage.removeItem('family_device_token');
          window.dispatchEvent(new CustomEvent('device-auth-required'));
          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          if (url.includes('/auth/pin-login') || url.includes('/auth/login') || url.includes('/kiosk/')) {
            return Promise.reject(error);
          }
          const hadToken = !!localStorage.getItem('auth_token');
          localStorage.removeItem('auth_token');
          if (hadToken) {
            window.location.href = '/';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  setAuthToken(token) {
    if (token) {
      this.client.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete this.client.defaults.headers.Authorization;
    }
  }

  // Generic HTTP methods
  async get(url, config = {}) {
    return this.client.get(url, config);
  }

  async post(url, data = {}, config = {}) {
    return this.client.post(url, data, config);
  }

  async put(url, data = {}, config = {}) {
    return this.client.put(url, data, config);
  }

  async delete(url, config = {}) {
    return this.client.delete(url, config);
  }

  async patch(url, data = {}, config = {}) {
    return this.client.patch(url, data, config);
  }

  // Authentication endpoints
  async login(username, password) {
    return this.post('/auth/login', { username, password });
  }


async logout() {
    return this.post('/auth/logout');
  }

  async register(userData) {
    return this.post('/auth/register', userData);
  }

  async changePassword(currentPassword, newPassword) {
    return this.post('/auth/change-password', { currentPassword, newPassword });
  }

  async getCurrentUser() {
    return this.get('/auth/me');
  }

  // User endpoints
  async getUsers(showArchived = false) {
    const params = showArchived ? '?show_archived=true' : '';
    return this.get(`/users${params}`);
  }

  async getUser(userId) {
    return this.get(`/users/${userId}`);
  }

  async updateUser(userId, userData) {
    return this.put(`/users/${userId}`, userData);
  }

  async deleteUser(userId) {
    return this.put(`/users/${userId}/archive`);
  }

  async hardDeleteUser(userId) {
    return this.delete(`/users/${userId}/permanent`);
  }

  async getUserStats(userId) {
    return this.get(`/users/${userId}/stats`);
  }


// Task endpoints
  async getTasks(userId = null, date = null) {
    const params = new URLSearchParams();
    if (userId) params.append('userId', userId);
    if (date) params.append('date', date);
    
    return this.get(`/tasks?${params.toString()}`);
  }

  async getTodayTasks(userId) {
    return this.get(`/tasks/today/${userId}`);
  }

  async createTask(taskData) {
    return this.post('/tasks', taskData);
  }

  async updateTask(taskId, taskData) {
    return this.put(`/tasks/${taskId}`, taskData);
  }

  async deleteTask(taskId) {
    return this.delete(`/tasks/${taskId}`);
  }

  async completeTask(taskId, completionMethod = 'manual', notes = null) {
    return this.post(`/tasks/${taskId}/complete`, { completion_method: completionMethod, notes });
  }

  async undoTaskCompletion(taskId, userId = null) {
    return this.delete(`/tasks/${taskId}/complete`, userId ? { data: { userId } } : {});
  }

  async getCompletionHistory() {
    return this.get('/tasks/completions/history');
  }

  async softDeleteCompletion(completionId) {
    return this.delete(`/tasks/completions/${completionId}`);
  }

  async getTaskCompletions(userId, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    return this.get(`/tasks/completions/${userId}?${params.toString()}`);
  }

  // Calendar endpoints
  async getCalendarEvents(startDate = null, endDate = null, userId = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (userId) params.append('userId', userId);
    
    return this.get(`/calendar/events?${params.toString()}`);
  }

  async createCalendarEvent(eventData) {
    return this.post('/calendar/events', eventData);
  }

  async updateCalendarEvent(eventId, eventData) {
    return this.put(`/calendar/events/${eventId}`, eventData);
  }

  async deleteCalendarEvent(eventId) {
    return this.delete(`/calendar/events/${eventId}`);
  }

  async getWeekView(userId = null, date = null) {
    const params = new URLSearchParams();
    if (userId) params.append('userId', userId);
    if (date) params.append('date', date);
    
    return this.get(`/calendar/week?${params.toString()}`);
  }

  // Analytics endpoints
  async getWeeklyProgress(userId, weekStart = null) {
    const params = new URLSearchParams();
    if (weekStart) params.append('weekStart', weekStart);
    
    return this.get(`/analytics/weekly-progress/${userId}?${params.toString()}`);
  }

  async getAchievements(userId) {
    return this.get(`/analytics/achievements/${userId}`);
  }

  async getDashboard(userId) {
    return this.get(`/analytics/dashboard/${userId}`);
  }

  async getFamilyOverview() {
    return this.get('/analytics/family-overview');
  }

  async sendNotification(title, message, topic = null, priority = 'default', tags = 'house,family') {
    return this.post('/integrations/notify', { title, message, topic, priority, tags });
  }

  async sendHomeAssistantEvent(event, data) {
    return this.post('/integrations/home-assistant', { event, data });
  }

  async getIntegrationSettings() {
    return this.get('/integrations/settings');
  }

  async updateIntegrationSettings(settings) {
    return this.post('/integrations/settings', { settings });
  }

  // Health check
  async healthCheck() {
    return this.get('/health');
  }

  // Family device auth endpoints
  async familyLogin(password, deviceName) {
    return this.post('/auth/family-login', { password, deviceName });
  }

  async verifyDevice() {
    return this.get('/auth/verify-device');
  }

  async familyLogout() {
    return this.post('/auth/family-logout');
  }

  async setFamilyPassword(password) {
    return this.post('/auth/set-family-password', { password });
  }

  async getFamilyDevices() {
    return this.get('/auth/family-devices');
  }

  async removeFamilyDevice(deviceId) {
    return this.delete(`/auth/family-devices/${deviceId}`);
  }

  // Setup endpoints
  async getSetupStatus() {
    return this.get('/auth/setup-status');
  }

  async validateInviteCode(inviteCode) {
    return this.post('/auth/validate-invite', { inviteCode });
  }

  async completeSetup(data) {
    return this.post('/auth/setup', data);
  }

  async resetForSetup(inviteCode) {
    return this.post('/auth/reset-for-setup', { inviteCode });
  }

  // Permission endpoints
  async getPermissions() {
    return this.get('/permissions');
  }

  async updatePermission(pageKey, required_role) {
    return this.put(`/permissions/${pageKey}`, { required_role });
  }

  // Shared goals endpoints
  async getSharedGoals() {
    return this.get('/shared-goals');
  }

  async getSharedGoalsHistory() {
    return this.get('/shared-goals/history');
  }

  async createSharedGoal(data) {
    return this.post('/shared-goals', data);
  }

  async updateSharedGoal(goalId, data) {
    return this.put(`/shared-goals/${goalId}`, data);
  }

  async deleteSharedGoal(goalId) {
    return this.delete(`/shared-goals/${goalId}`);
  }

  async rewardSharedGoal(goalId) {
    return this.post(`/shared-goals/${goalId}/reward`);
  }
}

const api = new ApiService();
export default api;